import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { mainnet } from 'viem/chains'
import { useConfig } from 'wagmi'

import { StakeDialogBaseProps, stakeDialogConfig } from './StakeDialog'
import { PROMPT_DELEGATE_AFTER_STAKE } from '@/config/chain/flags'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { predefinedDelegates } from '@/features/spk/governance/config'
import { afterStakeDelegateDialogConfig } from '@/features/spk/governance/dialogs/delegate/DelegateDialog'
import { delegateQueryFunction, delegateQueryKey } from '@/features/spk/governance/dialogs/delegate/logic/delegateQuery'

export type UseOpenStakeWithDelegateDialogParams = StakeDialogBaseProps & {
  successViewProceedAction?: () => void
  successViewProceedText?: string
  onSuccess?: () => void
}

export type OpenStakeWithDelegateDialogParams = StakeDialogBaseProps & {
  successViewProceedText?: string
  onSuccess?: () => void
}

export type OpenStakeWithDelegateDialogFunction = (params: OpenStakeWithDelegateDialogParams) => void

export type UseOpenStakeWithDelegateDialogResult = OpenStakeWithDelegateDialogFunction

export function useOpenStakeWithDelegateDialog(): UseOpenStakeWithDelegateDialogResult {
  const wagmiConfig = useConfig()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { isInSandbox } = useSandbox()
  const openDialog = useOpenDialog()

  const { data: delegateData } = useSuspenseQuery({
    queryKey: delegateQueryKey({
      delegator: (!isInSandbox && PROMPT_DELEGATE_AFTER_STAKE && account) || CheckedAddress.ZERO(),
      chainId: mainnet.id,
    }),
    queryFn:
      !isInSandbox && PROMPT_DELEGATE_AFTER_STAKE && account
        ? () => delegateQueryFunction({ delegator: account, chainId: mainnet.id, wagmiConfig })
        : () => null,
  })

  const shouldOpenDelegateDialog =
    delegateData && !predefinedDelegates.some((delegate) => delegate.address === delegateData.delegate)

  function openStakeDialog({
    successViewProceedText,
    onSuccess,
    ...stakeDialogProps
  }: OpenStakeWithDelegateDialogParams): void {
    function openDelegateDialog(spkStaked: NormalizedNumber): void {
      openDialog(afterStakeDelegateDialogConfig, {
        action: 'delegate' as const,
        variant: 'after-stake' as const,
        successViewProceedAction: undefined,
        successViewProceedText,
        onSuccess,
        staked: spkStaked,
      })
    }

    openDialog(stakeDialogConfig, {
      ...stakeDialogProps,
      successViewProceedText,
      onSuccess: shouldOpenDelegateDialog ? (spkStaked: NormalizedNumber) => openDelegateDialog(spkStaked) : onSuccess,
    } as any)
  }

  return openStakeDialog
}
